import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowClockwise, FrameCorners, Crosshair,
} from '@phosphor-icons/react';

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * 画像をふつうのビューアーのように見る部品。
 * ホイール（カーソル位置を中心に）／二本指のつまみで拡大、ドラッグで移動、
 * ダブルクリックで原寸⇔全体、下のボタンで −／＋／全体／原寸／回転。
 */
export function ZoomPanImage({ src, alt = '', heightClass = 'h-full', dark = true,
                              testId = 'zoom-pan' }) {
  const wrapRef = useRef(null);
  const [nat, setNat] = useState(null);                 // 画像の実寸 {w,h}
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const [rot, setRot] = useState(0);
  const [grabbing, setGrabbing] = useState(false);
  const pointers = useRef(new Map());
  const pinch = useRef(null);

  const fit = useCallback(() => {
    const el = wrapRef.current;
    if (!el || !nat) return;
    const cw = el.clientWidth; const ch = el.clientHeight;
    const s = Math.min(cw / nat.w, ch / nat.h);
    setView({ s, x: (cw - nat.w * s) / 2, y: (ch - nat.h * s) / 2 });
  }, [nat]);

  const actual = useCallback(() => {
    const el = wrapRef.current;
    if (!el || !nat) return;
    setView({ s: 1, x: (el.clientWidth - nat.w) / 2, y: (el.clientHeight - nat.h) / 2 });
  }, [nat]);

  useEffect(() => { setRot(0); }, [src]);
  useEffect(() => { fit(); }, [fit]);
  useEffect(() => {
    const on = () => fit();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [fit]);

  const zoomAt = useCallback((factor, cx, cy) => setView((v) => {
    const s = clamp(v.s * factor, 0.04, 16);
    const k = s / v.s;
    return { s, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k };
  }), []);

  // ホイールは passive で付くと preventDefault できず、ページごと動いてしまう。
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0018), e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (pointers.current.size === 2) {
      const [p, q] = [...pointers.current.values()];
      pinch.current = Math.hypot(p.x - q.x, p.y - q.y) || 1;
    } else { setGrabbing(true); }
  };

  const onPointerMove = (e) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size >= 2) {
      const [p, q] = [...pointers.current.values()];
      const d = Math.hypot(p.x - q.x, p.y - q.y) || 1;
      const base = pinch.current || d;
      pinch.current = d;
      const r = wrapRef.current.getBoundingClientRect();
      zoomAt(d / base, (p.x + q.x) / 2 - r.left, (p.y + q.y) / 2 - r.top);
      return;
    }
    const dx = e.clientX - prev.x; const dy = e.clientY - prev.y;
    setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) setGrabbing(false);
  };

  const btn = `p-1.5 rounded-md transition-colors ${dark
    ? 'text-zinc-200 hover:bg-white/15' : 'text-zinc-600 hover:bg-zinc-200'}`;

  return (
    <div className={`relative w-full ${heightClass} overflow-hidden touch-none select-none ${
      dark ? 'bg-zinc-950' : 'bg-zinc-100'}`} data-testid={testId}>
      <div ref={wrapRef} className="absolute inset-0"
        style={{ cursor: grabbing ? 'grabbing' : 'grab' }}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        onDoubleClick={() => (view.s < 0.98 ? actual() : fit())}
        data-testid={`${testId}-stage`}>
        {src && (
          <img src={src} alt={alt} draggable={false}
            onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            style={{
              position: 'absolute', left: 0, top: 0, width: nat ? nat.w : 'auto',
              maxWidth: 'none', transformOrigin: '0 0',
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.s}) rotate(${rot}deg)`,
              imageRendering: view.s > 1.2 ? 'auto' : '-webkit-optimize-contrast',
              background: '#fff',
            }}
            data-testid={`${testId}-img`} />
        )}
      </div>
      <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-0.5 rounded-xl border px-1.5 py-1 backdrop-blur ${
        dark ? 'border-white/15 bg-zinc-900/80' : 'border-zinc-200 bg-white/90 shadow-lg'}`}>
        <button type="button" className={btn} title="縮小" onClick={() => {
          const el = wrapRef.current;
          zoomAt(0.8, el.clientWidth / 2, el.clientHeight / 2);
        }} data-testid={`${testId}-out`}><MagnifyingGlassMinus size={16} /></button>
        <span className={`w-12 text-center text-[11px] tabular-nums ${dark ? 'text-zinc-300' : 'text-zinc-500'}`}
          data-testid={`${testId}-level`}>{Math.round(view.s * 100)}%</span>
        <button type="button" className={btn} title="拡大" onClick={() => {
          const el = wrapRef.current;
          zoomAt(1.25, el.clientWidth / 2, el.clientHeight / 2);
        }} data-testid={`${testId}-in`}><MagnifyingGlassPlus size={16} /></button>
        <span className={dark ? 'mx-1 h-4 w-px bg-white/15' : 'mx-1 h-4 w-px bg-zinc-200'} />
        <button type="button" className={`${btn} text-[11px] inline-flex items-center gap-1`} title="全体を表示"
          onClick={fit} data-testid={`${testId}-fit`}><FrameCorners size={15} /> 全体</button>
        <button type="button" className={`${btn} text-[11px] inline-flex items-center gap-1`} title="原寸で表示"
          onClick={actual} data-testid={`${testId}-actual`}><Crosshair size={15} /> 原寸</button>
        <button type="button" className={btn} title="回転"
          onClick={() => setRot((r) => (r + 90) % 360)} data-testid={`${testId}-rotate`}>
          <ArrowClockwise size={15} />
        </button>
      </div>
    </div>
  );
}

export default ZoomPanImage;
